import PropTypes from "prop-types";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { Paper } from "@mui/material";
import Grid from "@mui/material/Grid";

import { CustomerSelect } from "@/features/customers";
import { AllOutletsSelect, OutletRoleEnum, UserOutletsSelect } from "@/features/outlets";
import { CustomDatePicker } from "@/features/ui";

export const GeneralInformationSection = ({ editMode = false }) => {
  const { watch, control, setValue } = useFormContext();

  const seller = watch("seller");
  const buyerableType = watch("buyerableType");
  const salesInvoiceProductVariants = watch("salesInvoiceProductVariants");
  const salesInvoiceExpenses = watch("salesInvoiceExpenses");
  const salesDownPaymentAllocations = watch("salesDownPaymentAllocations");

  // Buyer determines which Sales Orders the products and down payments are
  // sourced from, so lock it only once those exist. Expenses don't depend on
  // the buyer, so adding an expense must not lock the buyer.
  const disableBuyer =
    salesInvoiceProductVariants.length > 0 ||
    salesDownPaymentAllocations.length > 0;

  // Seller (outlet) drives product sourcing, expense OCOA, and down payments,
  // so lock it once any of those exist.
  const disableSeller =
    salesInvoiceProductVariants.length > 0 ||
    salesInvoiceExpenses.length > 0 ||
    salesDownPaymentAllocations.length > 0;

  // Auto-set buyerableType based on seller's role
  useEffect(() => {
    if (seller) {
      const buyerableType =
        seller.role === OutletRoleEnum.HUB ? "OUTLET" : "CUSTOMER";
      setValue("buyerableType", buyerableType);
    }
  }, [seller, setValue]);

  useEffect(() => {
    if (editMode) return;

    if (buyerableType) {
      setValue("buyer", null);
    }
  }, [buyerableType, editMode, setValue]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="buyer"
            render={({ field, fieldState }) =>
              buyerableType === "OUTLET" ? (
                <AllOutletsSelect
                  value={field.value}
                  onChange={field.onChange}
                  disabled={editMode || disableBuyer}
                  allowedRoles={[OutletRoleEnum.RETAIL]}
                  TextFieldProps={{
                    label: "Pelanggan (Outlet Retail)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              ) : (
                <CustomerSelect
                  value={field.value}
                  onChange={field.onChange}
                  disabled={editMode || disableBuyer}
                  TextFieldProps={{
                    label: "Pelanggan (Retail)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              )
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="seller"
            render={({ field, fieldState }) => (
              <UserOutletsSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode || disableSeller}
                allowedRoles={[OutletRoleEnum.HUB]}
                TextFieldProps={{
                  label: "Pemasok",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            name="transactionDate"
            control={control}
            render={({ field, fieldState }) => (
              <CustomDatePicker
                value={field.value}
                onChange={field.onChange}
                required
                label="Tanggal Transaksi"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  disableFuture: true,
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  editMode: PropTypes.bool,
};
